import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.mjs';
import * as React from 'react';
import memoize from 'lodash/memoize';
import throttle from 'lodash/throttle';
import { getDocumentScrollElement } from '../../helpers/getDocumentScrollElement.mjs';
import { getParentScrollElement } from '../../helpers/getParentScrollElement.mjs';
import { useIsMobile } from '../../hooks/useIsMobile.mjs';

var memoizedGetScrollElement = memoize(getDocumentScrollElement);
var useProps = function useProps(props) {
  var refThis = React.useRef(null);
  var refParallax = React.useRef(null);
  var isMobile = useIsMobile();
  var _React$useState = React.useState(0),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    targetParallaxScrollHeight = _React$useState2[0],
    setTargetParallaxScrollHeight = _React$useState2[1];
  var _React$useState3 = React.useState(props.enableScrollParallax ? 0 : 1),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    titleOpacity = _React$useState4[0],
    setTitleOpacity = _React$useState4[1];
  var _React$useState5 = React.useState(props.enableScrollParallax ? 1 : 0),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    parallaxOpacity = _React$useState6[0],
    setParallaxOpacity = _React$useState6[1];
  var _React$useState7 = React.useState(0),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    sideElementWidth = _React$useState8[0],
    setSideElementWidth = _React$useState8[1];
  var titleAlignmentState = React.useMemo(function () {
    return (isMobile && !props.ignoreMobileTitleAlignment ? "Left" : props.titleAlignment) || "Left";
  }, [isMobile, props.titleAlignment, props.ignoreMobileTitleAlignment]);
  var _React$useState9 = React.useState(0),
    _React$useState10 = _slicedToArray(_React$useState9, 2),
    parentWidth = _React$useState10[0],
    setParentWidth = _React$useState10[1];
  var _React$useState11 = React.useState(Boolean(props.enableScrollParallax && props.expendScrollParallaxElement)),
    _React$useState12 = _slicedToArray(_React$useState11, 2),
    isParallaxVisible = _React$useState12[0],
    setParallaxVisible = _React$useState12[1];
  var scrollElement = memoizedGetScrollElement(refThis.current);
  var handleParallaxVisibility = React.useCallback(function (visible) {
    var _a;
    setParallaxVisible(visible);
    (_a = props.onChangeParallaxVisible) === null || _a === void 0 ? void 0 : _a.call(props, visible);
  }, [props.onChangeParallaxVisible]);
  var calcParallaxOpacity = React.useCallback(function (scrollPos, throttleOffset) {
    var _a;
    var titleDisappearOffset = (_a = props.titleContainerDisappearPosition) !== null && _a !== void 0 ? _a : throttleOffset;
    var value = scrollPos / throttleOffset;
    value = parseFloat(value.toFixed(2));
    var rangeGuardValue = value > 1 ? 1 : value < 0 ? 0 : value;
    if (scrollPos >= titleDisappearOffset) {
      setTitleOpacity(rangeGuardValue);
    } else if (scrollPos < titleDisappearOffset && titleOpacity > rangeGuardValue) {
      setTitleOpacity(rangeGuardValue);
    }
    if (scrollPos >= throttleOffset) {
      setParallaxOpacity(1 - rangeGuardValue);
    } else if (scrollPos < throttleOffset && parallaxOpacity < 1 - rangeGuardValue) {
      setParallaxOpacity(1 - rangeGuardValue);
    }
  }, [parallaxOpacity, props.titleContainerDisappearPosition, titleOpacity]);
  var handleScroll = React.useMemo(function () {
    return throttle(function () {
      requestAnimationFrame(function () {
        var _a;
        if (!props.enableScrollParallax) {
          return;
        }
        if (!props.expendScrollParallaxElement) {
          handleParallaxVisibility(false);
        }
        var scrollHeight = targetParallaxScrollHeight;
        var scrollingElement = scrollElement;
        if (scrollingElement) {
          var scrollTop = scrollingElement.scrollTop;
          var offset = (_a = props.parallaxDisappearPosition) !== null && _a !== void 0 ? _a : scrollHeight;
          calcParallaxOpacity(scrollTop, offset);
          if (scrollTop >= scrollHeight) {
            handleParallaxVisibility(false);
          } else {
            handleParallaxVisibility(true);
          }
        }
      });
    }, 120);
  }, [calcParallaxOpacity, handleParallaxVisibility, props.expendScrollParallaxElement, props.parallaxDisappearPosition, props.enableScrollParallax, scrollElement, targetParallaxScrollHeight]);
  var handleResize = React.useCallback(function (width) {
    if (width !== undefined) {
      setParentWidth(width);
    }
  }, []);
  var handleResizeSideWidth = React.useCallback(function (width) {
    if (width !== undefined && titleAlignmentState === "Center") {
      setSideElementWidth(Math.max(sideElementWidth, width));
    }
  }, [titleAlignmentState, sideElementWidth]);
  React.useLayoutEffect(function () {
    var _a;
    var parentScrollElement = (_a = getParentScrollElement((refThis === null || refThis === void 0 ? void 0 : refThis.current) || null)) !== null && _a !== void 0 ? _a : window;
    parentScrollElement.addEventListener("scroll", handleScroll);
    return function () {
      parentScrollElement.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);
  React.useLayoutEffect(function () {
    var targetParallax = refParallax.current;
    if (targetParallax) {
      setTargetParallaxScrollHeight(targetParallax.scrollHeight);
    }
  }, [refParallax]);
  return Object.assign(Object.assign({}, props), {
    titleOpacity: titleOpacity,
    parallaxOpacity: parallaxOpacity,
    handleScroll: handleScroll,
    isParallaxVisible: isParallaxVisible,
    titleAlignmentState: titleAlignmentState,
    parentWidth: parentWidth,
    refParallax: refParallax,
    refThis: refThis,
    sideElementWidth: sideElementWidth,
    handleResize: handleResize,
    handleResizeSideWidth: handleResizeSideWidth
  });
};

export { useProps };
